// Typed error taxonomy for the at-rest decrypt chokepoint (ADR-0061 Part 1c).
//
// `decryptSecret` fans into failures whose operator responses are OPPOSITE, so a
// single generic Error is actively dangerous: a customer crypto-shred (scheme c,
// working as designed — do nothing) must be distinguishable from a platform-CMK
// IAM regression (scheme k unavailable — page, every tenant is down). The scope
// tag is the machine-readable form of that "who does this affect / what do I do"
// question; the code is the stable discriminant.
//
// Part 4 (removing the silent plaintext fallback) pushes MORE failures through
// this chokepoint, which is why the taxonomy is defined here in the reader
// increment rather than bolted on later.

/**
 * Blast radius of a decrypt failure — what an operator must reach for.
 *
 *   - `row`         one bad row; a data bug, investigate that row.
 *   - `app-key`     the app-key keyring can't resolve the writing key; a mid-drain
 *                   misconfiguration (or v1 corruption) — check the keyring.
 *   - `all-tenants` a platform-owned KEK is unavailable; a platform-wide decrypt
 *                   outage — PAGE.
 *   - `one-org`     a single customer's KEK is unavailable; usually contractual
 *                   (they revoked/rotated) — notify the customer, do not page.
 *   - `caller`      the caller misused the API (e.g. a v2 blob with no AAD
 *                   context); a programming error, not an operational one. Kept
 *                   OUT of the operator-facing set on purpose.
 */
export type SecretErrorScope = 'row' | 'app-key' | 'all-tenants' | 'one-org' | 'caller';

/**
 * The stable discriminant. The first five map 1:1 to the ADR-0061 Part 1c table;
 * `DecryptContextRequired` is the caller-misuse code (a v2 envelope reached
 * `decryptSecret` without the AAD context needed to authenticate it).
 */
export type SecretErrorCode =
  | 'MalformedEnvelope'
  | 'UnknownScheme'
  | 'AppKeyNotInKeyring'
  | 'PlatformKekUnavailable'
  | 'CustomerKekUnavailable'
  | 'AeadAuthFailure'
  | 'DecryptContextRequired';

/** Base class for every typed decrypt failure. `instanceof` catches the family. */
export abstract class SecretDecryptError extends Error {
  abstract readonly code: SecretErrorCode;
  abstract readonly scope: SecretErrorScope;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    // Preserve the concrete subclass name across the Error super() call.
    this.name = new.target.name;
  }
}

/** The envelope could not be parsed (bad prefix layout, bad base64, bad CBOR). One row. */
export class MalformedEnvelopeError extends SecretDecryptError {
  readonly code = 'MalformedEnvelope' as const;
  readonly scope = 'row' as const;
}

/** The v2 header declared a scheme this build does not recognise. One row. */
export class UnknownSchemeError extends SecretDecryptError {
  readonly code = 'UnknownScheme' as const;
  readonly scope = 'row' as const;
}

/**
 * A scheme-`a`/v1 blob's writing key is not in the keyring (its `kid` is
 * unresolved, or every keyring key failed to authenticate a v1 blob). Mid-drain
 * misconfiguration or corruption — check the keyring, do NOT page the fleet.
 */
export class AppKeyNotInKeyringError extends SecretDecryptError {
  readonly code = 'AppKeyNotInKeyring' as const;
  readonly scope = 'app-key' as const;
}

/**
 * A scheme-`k` (platform-CMK-wrapped DEK) blob could not be unwrapped — the
 * platform KEK is unavailable (IAM/throttle), or, in this increment, no scheme-`k`
 * resolver is registered yet (ADR-0062 increment C). Affects EVERY tenant. PAGE.
 */
export class PlatformKekUnavailableError extends SecretDecryptError {
  readonly code = 'PlatformKekUnavailable' as const;
  readonly scope = 'all-tenants' as const;
}

/**
 * A scheme-`c` (customer-CMK-wrapped DEK) blob could not be unwrapped — one
 * customer's KEK is unavailable. Usually contractual (revoked/rotated key, i.e. a
 * working-as-designed crypto-shred). Affects ONE org — notify the customer,
 * do not page.
 */
export class CustomerKekUnavailableError extends SecretDecryptError {
  readonly code = 'CustomerKekUnavailable' as const;
  readonly scope = 'one-org' as const;
}

/**
 * AES-GCM authentication failed on a blob whose key was resolved — the AAD does
 * not match (wrong context, or storage tampering that altered the ciphertext,
 * header, or bound context). Do NOT proceed with the value. One row.
 */
export class AeadAuthFailureError extends SecretDecryptError {
  readonly code = 'AeadAuthFailure' as const;
  readonly scope = 'row' as const;
}

/**
 * A v2 envelope reached `decryptSecret` without the `DecryptContext` needed to
 * reconstruct its AAD. This is a CALLER bug (a read site that did not thread the
 * org/table/column/field/row through) — never a stored-data or operational fault,
 * which is why its scope is `caller` and it is excluded from the operator table.
 */
export class DecryptContextRequiredError extends SecretDecryptError {
  readonly code = 'DecryptContextRequired' as const;
  readonly scope = 'caller' as const;
}

/** Narrow an unknown thrown value to the typed-decrypt-error family. */
export function isSecretDecryptError(err: unknown): err is SecretDecryptError {
  return err instanceof SecretDecryptError;
}
